import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';
import {
  OrganizationAccessDialogComponent,
  OrganizationAccessDialogData,
} from 'src/app/dialogs/organization-access-dialog/organization-access-dialog.component';
import { TrademarkBulkSynchronizationError } from 'src/app/models/trademarkBulkSynchronization';
import { synchronizationFailureKey } from 'src/app/helpers/trademark-synchronization.helper';
import { AuthenticationService } from 'src/app/services/authentication.service';
import { OrganizationAccessService } from 'src/app/services/organization-access.service';

export interface TrademarksBulkSynchronizationErrorsDialogData {
  errors: TrademarkBulkSynchronizationError[];
  // Number of trademarks in error according to the report
  total?: number;
  // The organisation of the bulk synchronisation, entered to open one of its trademarks
  organization?: { id: string, name: string };
  // Whether the session may enter that organisation, and why not right now (translation key)
  canEnter?: boolean;
  enterBlockedReason?: string;
}

@Component({
  selector: 'app-trademarks-bulk-synchronization-errors-dialog',
  templateUrl: './trademarks-bulk-synchronization-errors-dialog.component.html',
  styleUrl: './trademarks-bulk-synchronization-errors-dialog.component.scss'
})
export class TrademarksBulkSynchronizationErrorsDialogComponent implements OnInit {
  errors: TrademarkBulkSynchronizationError[] = [];
  // Number of trademarks in error according to the report, which may list fewer errors
  total: number = 0;
  displayedColumns: string[] = ['name', 'countryOfDesignation', 'identifierNumber', 'error', 'actions'];
  // The session already acts inside the organisation of the report
  insideOrganization: boolean = false;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: TrademarksBulkSynchronizationErrorsDialogData,
    private authService: AuthenticationService,
    private dialog: MatDialog,
    private dialogRef: MatDialogRef<TrademarksBulkSynchronizationErrorsDialogComponent>,
    private organizationAccessService: OrganizationAccessService,
    private router: Router
  ) {

  }

  ngOnInit(): void {
    this.errors = this.data.errors || [];
    this.total = Math.max(typeof this.data.total === 'number' ? this.data.total : 0, this.errors.length);
    this.insideOrganization = this.isInsideOrganization();
  }

  failureKey(error: TrademarkBulkSynchronizationError): string {
    return synchronizationFailureKey(error.error);
  }

  hasDetail(error: TrademarkBulkSynchronizationError): boolean {
    return !!error.detail && (!!error.detail.ours || !!error.detail.office);
  }

  canOpen(): boolean {
    return this.insideOrganization || (!!this.data.organization && !!this.data.canEnter && !this.data.enterBlockedReason);
  }

  openTrademark(error: TrademarkBulkSynchronizationError) {
    const id = error.trademark?._id;
    if (!id || !this.canOpen()) return;
    const url = `/trademarks/${id}`;
    if (this.insideOrganization) {
      this.dialogRef.close();
      this.router.navigateByUrl(url);
      return;
    }
    if (!this.data.organization) return;
    const data: OrganizationAccessDialogData = { organization: this.data.organization, returnUrl: url };
    this.dialog.open(OrganizationAccessDialogComponent, {
      panelClass: 'dialog-container',
      width: '520px',
      data
    });
  }

  onCancel() {
    this.dialogRef.close()
  }

  private isInsideOrganization(): boolean {
    const organizationId = this.data.organization?.id;
    if (!organizationId) return false;
    const access = this.organizationAccessService.current();
    if (access) return access.organization.id === organizationId;
    return this.authService.currentUser()?.organizationId === organizationId;
  }

}
